import { useMemo, useState, useCallback } from 'react';
import * as LucideIcons from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  Package,
  Settings2,
  LayoutGrid,
  Wrench,
  Truck,
  BarChart3,
  Plus,
  X,
  Pencil,
  Cloud,
} from 'lucide-react';
import { DataTable, PageHeader } from '@gs/ui';
import type { Column } from '@gs/ui';
import { useToast } from '@gs/ui';
import { api } from '../services/api';
import { useQuery } from '@tanstack/react-query';
import {
  useAdminUsers,
  useCreateUser,
  useUpdateUserAppRole,
  useUpdateUserServicePoints,
  useUpdateUserStatus,
  type AdminUser,
  type GsAppRole,
} from '../hooks/useUsers';
import { PERSONAS, type Persona } from '../data/personas';

const KNOWN_APPS: { key: string; label: string; icon: LucideIcon; hasSites: boolean }[] = [
  { key: 'inventaire', label: 'GS-Inventaire', icon: Package, hasSites: true },
  { key: 'admin', label: 'GS-Admin', icon: Settings2, hasSites: false },
  { key: 'hub', label: 'GS-Hub', icon: LayoutGrid, hasSites: false },
  { key: 'shophub', label: 'GS-ShopHub', icon: Wrench, hasSites: false },
  { key: 'flotte', label: 'GS-Flotte', icon: Truck, hasSites: false },
  { key: 'analytics', label: 'GS-Analytics', icon: BarChart3, hasSites: false },
];

const ROLE_BADGE_CLASSES: Record<string, string> = {
  ADMIN: 'bg-violet-100 text-violet-700 dark:bg-violet-950/30 dark:text-violet-300',
  GESTIONNAIRE: 'bg-blue-100 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300',
  UTILISATEUR: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
};

function roleBadgeClasses(role: string) {
  return ROLE_BADGE_CLASSES[role] ?? ROLE_BADGE_CLASSES.UTILISATEUR;
}

function appLabel(key: string) {
  return KNOWN_APPS.find((a) => a.key === key)?.label ?? key;
}

function AppRoleBadge({ appKey, role }: { appKey: string; role: string }) {
  const badgeCls = roleBadgeClasses(role);
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold mr-1 mb-1 ${badgeCls}`}
    >
      <span className="opacity-80 font-normal">{appLabel(appKey)}</span>
      <span>{role}</span>
    </span>
  );
}

function PersonaLucideIcon({ name, className }: { name: string; className?: string }) {
  const Cmp = (LucideIcons as Record<string, LucideIcon>)[name];
  if (!Cmp) return null;
  return <Cmp size={14} className={className} />;
}

type ServicePointRow = { id: string; name: string };

export interface AzureAdUserRow {
  id: string;
  displayName: string;
  mail: string;
  jobTitle: string;
}

const GS_EMAIL_SUFFIX = '@groupesignalisation.ca';

const emptyRoles = () => Object.fromEntries(KNOWN_APPS.map((a) => [a.key, '']));

export default function UsersPage() {
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isCreate, setIsCreate] = useState(false);
  const [draftSp, setDraftSp] = useState<string[]>([]);

  const [newEmail, setNewEmail] = useState('');
  const [newFullName, setNewFullName] = useState('');
  const [newRoles, setNewRoles] = useState<Record<string, string>>(emptyRoles);

  const [cloneFromId, setCloneFromId] = useState('');
  const [showAzureImport, setShowAzureImport] = useState(false);
  const [azureSelected, setAzureSelected] = useState<Set<string>>(() => new Set());

  const { data: users, isLoading, isError, refetch } = useAdminUsers();
  const { data: sites = [] } = useQuery({
    queryKey: ['admin-inventory-service-points'],
    queryFn: () => api.get<ServicePointRow[]>('/api/inventory/service-points'),
  });

  const { data: azureUsers = [], isPending: azureUsersLoading } = useQuery({
    queryKey: ['azure-ad-users'],
    queryFn: () => api.get<AzureAdUserRow[]>('/api/azure-ad/users'),
    enabled: showAzureImport,
  });

  const createUser = useCreateUser();
  const updateAppRole = useUpdateUserAppRole();
  const updateSp = useUpdateUserServicePoints();
  const updateStatus = useUpdateUserStatus();
  const { toast } = useToast();

  const selectedUser = useMemo(
    () => (selectedUserId ? users?.find((u) => u.id === selectedUserId) : null),
    [users, selectedUserId]
  );

  const notYetImported = useMemo(() => {
    if (!users) return [];
    if (!azureUsers.length) return [];
    return azureUsers.filter((au) => {
      const m = au.mail?.toLowerCase().trim();
      if (!m) return false;
      return !users.some((u) => u.email.toLowerCase() === m);
    });
  }, [users, azureUsers]);

  const openAzureImport = () => {
    setShowAzureImport(true);
    setAzureSelected(new Set());
  };

  const openPanel = (u: AdminUser) => {
    setIsCreate(false);
    setSelectedUserId(u.id);
    setDraftSp([...u.servicePoints]);
  };

  const openCreate = () => {
    setSelectedUserId(null);
    setIsCreate(true);
    setDraftSp([]);
    setNewEmail('');
    setNewFullName('');
    setNewRoles(emptyRoles());
    setCloneFromId('');
  };

  const closePanel = () => {
    setSelectedUserId(null);
    setIsCreate(false);
  };

  const applyPersona = useCallback(
    (p: Persona) => {
      const roles = emptyRoles();
      for (const ar of p.appRoles) {
        roles[ar.appKey] = ar.role;
      }
      setNewRoles(roles);
      if (p.allSites) {
        setDraftSp(sites.map((s) => s.id));
      } else {
        setDraftSp([]);
      }
    },
    [sites]
  );

  const applyClone = useCallback(
    (userId: string) => {
      const source = users?.find((u) => u.id === userId);
      if (!source) return;
      const roles = emptyRoles();
      for (const ar of source.appRoles) {
        roles[ar.appKey] = ar.role;
      }
      setNewRoles(roles);
      setDraftSp([...source.servicePoints]);
    },
    [users]
  );

  const toggleAzureRow = (id: string) => {
    setAzureSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllAzure = () => {
    setAzureSelected(new Set(notYetImported.map((u) => u.id)));
  };

  const handleAzureBatchImport = async () => {
    const list = notYetImported.filter((au) => azureSelected.has(au.id));
    if (list.length === 0) return;
    let ok = 0;
    for (const au of list) {
      const mail = au.mail?.toLowerCase().trim();
      if (!mail) continue;
      try {
        await createUser.mutateAsync({
          email: mail,
          fullName: au.displayName.trim() || mail,
          appRoles: [],
          servicePoints: [],
        });
        ok += 1;
      } catch {
        /* doublon ou erreur réseau */
      }
    }
    setShowAzureImport(false);
    setAzureSelected(new Set());
    toast(
      ok > 0
        ? `${ok} utilisateur(s) importé(s) — assignez leurs accès`
        : 'Aucun import réussi (déjà présents ou erreur)',
      ok > 0 ? 'success' : 'info'
    );
  };

  const roleForApp = (u: AdminUser | null | undefined, appKey: string) =>
    u?.appRoles.find((r) => r.appKey === appKey)?.role ?? '';

  const handleAppRoleChange = (appKey: string, value: string) => {
    if (!selectedUser) return;
    const role = value === '' ? null : (value as GsAppRole);
    updateAppRole.mutate(
      { userId: selectedUser.id, appKey, role },
      {
        onSuccess: (data) => {
          setDraftSp([...data.servicePoints]);
          toast('Rôle mis à jour', 'success');
        },
        onError: (e) => toast(e instanceof Error ? e.message : 'Erreur', 'error'),
      }
    );
  };

  const handleSaveSites = () => {
    if (!selectedUser) return;
    updateSp.mutate(
      { userId: selectedUser.id, servicePoints: draftSp },
      {
        onSuccess: () => toast('Sites enregistrés', 'success'),
        onError: (e) => toast(e instanceof Error ? e.message : 'Erreur', 'error'),
      }
    );
  };

  const handleStatusToggle = () => {
    if (!selectedUser) return;
    updateStatus.mutate(
      { userId: selectedUser.id, isActive: !selectedUser.isActive },
      {
        onSuccess: () => toast('Statut mis à jour', 'success'),
        onError: (e) => toast(e instanceof Error ? e.message : 'Erreur', 'error'),
      }
    );
  };

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const emailNorm = newEmail.trim().toLowerCase();
    if (!emailNorm.endsWith(GS_EMAIL_SUFFIX)) {
      toast(`Courriel doit finir par ${GS_EMAIL_SUFFIX}`, 'error');
      return;
    }
    if (newFullName.trim().length < 2) {
      toast('Nom trop court', 'error');
      return;
    }
    const appRoles: { appKey: string; role: GsAppRole }[] = [];
    for (const app of KNOWN_APPS) {
      const r = newRoles[app.key];
      if (r === 'ADMIN' || r === 'GESTIONNAIRE' || r === 'UTILISATEUR') {
        appRoles.push({ appKey: app.key, role: r });
      }
    }
    const invRole = newRoles.inventaire;
    const servicePoints =
      invRole === 'GESTIONNAIRE' || invRole === 'UTILISATEUR' ? [...draftSp] : [];

    createUser.mutate(
      { email: emailNorm, fullName: newFullName.trim(), appRoles, servicePoints },
      {
        onSuccess: () => {
          toast('Utilisateur créé', 'success');
          closePanel();
        },
        onError: (e) => toast(e instanceof Error ? e.message : 'Erreur', 'error'),
      }
    );
  };

  const columns: Column<AdminUser>[] = [
    {
      key: 'fullName',
      header: 'Nom',
      render: (r) => <span className="font-medium text-slate-800">{r.fullName}</span>,
    },
    { key: 'email', header: 'Email' },
    {
      key: 'apps',
      header: 'Accès apps',
      render: (r) => (
        <div className="flex flex-wrap max-w-md">
          {r.appRoles.length === 0 ? (
            <span className="text-slate-400 text-sm">—</span>
          ) : (
            r.appRoles.map((ar) => <AppRoleBadge key={`${r.id}-${ar.appKey}`} appKey={ar.appKey} role={ar.role} />)
          )}
        </div>
      ),
    },
    {
      key: 'isActive',
      header: 'Statut',
      render: (r) => (
        <span className={r.isActive ? 'text-emerald-600 font-medium' : 'text-slate-400'}>
          {r.isActive ? 'Actif' : 'Inactif'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            openPanel(r);
          }}
          className="text-slate-400 hover:text-orange-500 inline-flex items-center gap-1 text-sm"
        >
          <Pencil size={15} />
          Gérer
        </button>
      ),
    },
  ];

  const showInvSites =
    (isCreate && (newRoles.inventaire === 'GESTIONNAIRE' || newRoles.inventaire === 'UTILISATEUR')) ||
    (!isCreate &&
      selectedUser &&
      (roleForApp(selectedUser, 'inventaire') === 'GESTIONNAIRE' ||
        roleForApp(selectedUser, 'inventaire') === 'UTILISATEUR'));

  if (isError) {
    return (
      <div className="space-y-4 p-4">
        <PageHeader title="Utilisateurs" />
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-4 py-3 text-sm">
          Impossible de charger les utilisateurs.
          <button type="button" className="ml-3 underline" onClick={() => refetch()}>
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Utilisateurs"
        description="Gestion centralisée des accès à toutes les applications GS"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={openAzureImport}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              <Cloud size={16} />
              Importer Azure AD
            </button>
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center gap-2 rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-600"
            >
              <Plus size={18} />
              Ajouter
            </button>
          </div>
        }
      />

      {isLoading ? (
        <div className="space-y-3 animate-pulse">
          <div className="h-10 bg-slate-200 rounded-lg" />
          <div className="h-64 bg-slate-100 rounded-xl" />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden dark:border-slate-700 dark:bg-slate-800">
          <DataTable<AdminUser>
            columns={columns}
            data={users ?? []}
            rowKey={(r) => r.id}
            emptyMessage="Aucun utilisateur"
            onRowClick={openPanel}
          />
        </div>
      )}

      {showAzureImport && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          onClick={() => setShowAzureImport(false)}
        >
          <div className="fixed inset-0 bg-black/40" />
          <div
            className="relative z-10 w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col rounded-xl border border-slate-200 bg-white shadow-md dark:border-slate-600 dark:bg-slate-900"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-700">
              <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Importer depuis Azure AD</h2>
              <button
                type="button"
                onClick={() => setShowAzureImport(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1 space-y-3">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Utilisateurs non encore présents dans DIM_Users. Import sans rôle — à configurer après.
              </p>
              {azureUsersLoading ? (
                <p className="text-sm text-slate-500 py-4 text-center">Chargement…</p>
              ) : notYetImported.length === 0 ? (
                <p className="text-sm text-slate-600 dark:text-slate-300 py-4 text-center">
                  Tous les comptes listés sont déjà importés.
                </p>
              ) : (
                <>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={selectAllAzure}
                      className="text-sm font-medium text-orange-600 hover:text-orange-700"
                    >
                      Sélectionner tout
                    </button>
                    <button
                      type="button"
                      onClick={() => setAzureSelected(new Set())}
                      className="text-sm text-slate-500 hover:text-slate-700"
                    >
                      Effacer
                    </button>
                  </div>
                  <ul className="space-y-2">
                    {notYetImported.map((au) => (
                      <li
                        key={au.id}
                        className="flex items-start gap-3 rounded-lg border border-slate-100 px-3 py-2 dark:border-slate-700"
                      >
                        <input
                          type="checkbox"
                          checked={azureSelected.has(au.id)}
                          onChange={() => toggleAzureRow(au.id)}
                          className="mt-1 rounded border-slate-300 text-orange-500"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-slate-800 dark:text-slate-100">{au.displayName}</div>
                          <div className="text-xs text-slate-500">{au.mail}</div>
                          {au.jobTitle && (
                            <div className="text-xs text-slate-400 mt-0.5">{au.jobTitle}</div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
            <div className="flex gap-2 border-t border-slate-200 p-4 dark:border-slate-700">
              <button
                type="button"
                onClick={() => setShowAzureImport(false)}
                className="flex-1 rounded-lg border border-slate-200 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200"
              >
                Annuler
              </button>
              <button
                type="button"
                disabled={azureSelected.size === 0 || createUser.isPending}
                onClick={() => void handleAzureBatchImport()}
                className="flex-1 rounded-lg bg-orange-500 py-2 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-50"
              >
                Importer ({azureSelected.size})
              </button>
            </div>
          </div>
        </div>
      )}

      {(isCreate || selectedUser) && (
        <div className="fixed inset-0 z-50 flex justify-end" onClick={closePanel}>
          <div className="fixed inset-0 bg-black/30" />
          <div
            className="relative w-full max-w-lg bg-white h-full overflow-y-auto shadow-md border-l border-slate-200 dark:border-slate-700 dark:bg-slate-900"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between z-10 dark:bg-slate-900 dark:border-slate-700">
              <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                {isCreate ? 'Nouvel utilisateur' : 'Détail utilisateur'}
              </h2>
              <button type="button" onClick={closePanel} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>

            {isCreate ? (
              <form onSubmit={handleCreateSubmit} className="p-4 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Nom complet
                  </label>
                  <input
                    value={newFullName}
                    onChange={(e) => setNewFullName(e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                    required
                    minLength={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Courriel {GS_EMAIL_SUFFIX}
                  </label>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                    required
                  />
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-2">
                    Copier les accès d&apos;un utilisateur existant
                  </h3>
                  <select
                    value={cloneFromId}
                    onChange={(e) => {
                      const v = e.target.value;
                      setCloneFromId(v);
                      if (v) applyClone(v);
                    }}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                  >
                    <option value="">— Sélectionner un utilisateur —</option>
                    {users?.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.fullName} ({u.email})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-2">
                    Profil prédéfini (optionnel)
                  </h3>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    {PERSONAS.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => applyPersona(p)}
                        className="text-left p-2.5 rounded-lg border border-slate-200 hover:border-orange-300 hover:bg-orange-50 transition-colors dark:border-slate-600 dark:hover:bg-orange-950/30"
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md"
                            style={{ backgroundColor: p.color }}
                          >
                            <PersonaLucideIcon name={p.icon} className="text-slate-700" />
                          </span>
                          <div className="min-w-0">
                            <div className="text-xs font-semibold text-slate-800 dark:text-slate-100">{p.label}</div>
                            <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2">
                              {p.description}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3">
                    Accès par application
                  </h3>
                  <div className="space-y-3">
                    {KNOWN_APPS.map((app) => {
                      const Icon = app.icon;
                      return (
                        <div key={app.key} className="flex items-center gap-3 flex-wrap">
                          <Icon size={18} className="text-slate-500 shrink-0" />
                          <span className="text-sm text-slate-700 dark:text-slate-300 w-40 shrink-0">{app.label}</span>
                          <select
                            value={newRoles[app.key] ?? ''}
                            onChange={(e) =>
                              setNewRoles((prev) => ({ ...prev, [app.key]: e.target.value }))
                            }
                            className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm flex-1 min-w-[140px] dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                          >
                            <option value="">Aucun</option>
                            <option value="UTILISATEUR">UTILISATEUR</option>
                            <option value="GESTIONNAIRE">GESTIONNAIRE</option>
                            <option value="ADMIN">ADMIN</option>
                          </select>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {(newRoles.inventaire === 'GESTIONNAIRE' || newRoles.inventaire === 'UTILISATEUR') && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-2">
                      Points de service (Inventaire)
                    </h3>
                    <div className="max-h-48 overflow-y-auto space-y-2 border border-slate-100 rounded-lg p-3 dark:border-slate-700">
                      {sites.map((s) => (
                        <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={draftSp.includes(s.id)}
                            onChange={() =>
                              setDraftSp((prev) =>
                                prev.includes(s.id) ? prev.filter((x) => x !== s.id) : [...prev, s.id]
                              )
                            }
                            className="rounded border-slate-300 text-orange-500"
                          />
                          <span className="dark:text-slate-200">{s.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={closePanel}
                    className="flex-1 border border-slate-200 rounded-lg py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={createUser.isPending}
                    className="flex-1 bg-orange-500 text-white rounded-lg py-2.5 text-sm font-semibold hover:bg-orange-600 disabled:opacity-60"
                  >
                    Créer
                  </button>
                </div>
              </form>
            ) : (
              selectedUser && (
                <div className="p-4 space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="h-14 w-14 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center text-lg font-semibold">
                      {selectedUser.avatarInitials || selectedUser.fullName.slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 dark:text-slate-100">{selectedUser.fullName}</p>
                      <p className="text-sm text-slate-500">{selectedUser.email}</p>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3">
                      Accès par application
                    </h3>
                    <div className="space-y-3">
                      {KNOWN_APPS.map((app) => {
                        const Icon = app.icon;
                        const current = roleForApp(selectedUser, app.key);
                        return (
                          <div key={app.key}>
                            <div className="flex items-center gap-3 flex-wrap">
                              <Icon size={18} className="text-slate-500 shrink-0" />
                              <span className="text-sm text-slate-700 dark:text-slate-300 w-40 shrink-0">
                                {app.label}
                              </span>
                              <select
                                value={current}
                                onChange={(e) => handleAppRoleChange(app.key, e.target.value)}
                                disabled={updateAppRole.isPending}
                                className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm flex-1 min-w-[140px] dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                              >
                                <option value="">Aucun</option>
                                <option value="UTILISATEUR">UTILISATEUR</option>
                                <option value="GESTIONNAIRE">GESTIONNAIRE</option>
                                <option value="ADMIN">ADMIN</option>
                              </select>
                            </div>
                            {app.key === 'inventaire' && showInvSites && (
                              <div className="mt-3 ml-7 pl-2 border-l-2 border-slate-100 dark:border-slate-700">
                                <p className="text-xs text-slate-500 mb-2">Sites assignés</p>
                                <div className="max-h-40 overflow-y-auto space-y-2">
                                  {sites.map((s) => (
                                    <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={draftSp.includes(s.id)}
                                        onChange={() =>
                                          setDraftSp((prev) =>
                                            prev.includes(s.id)
                                              ? prev.filter((x) => x !== s.id)
                                              : [...prev, s.id]
                                          )
                                        }
                                        className="rounded border-slate-300 text-orange-500"
                                      />
                                      <span>{s.name}</span>
                                    </label>
                                  ))}
                                </div>
                                <button
                                  type="button"
                                  onClick={handleSaveSites}
                                  disabled={updateSp.isPending}
                                  className="mt-3 text-sm font-medium text-orange-600 hover:text-orange-700"
                                >
                                  Enregistrer les sites
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3">Informations</h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-3 dark:border-slate-600">
                        <span className="text-slate-700 dark:text-slate-300">Compte actif</span>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={selectedUser.isActive}
                          onClick={handleStatusToggle}
                          disabled={updateStatus.isPending}
                          className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${
                            selectedUser.isActive ? 'bg-orange-500' : 'bg-slate-200'
                          }`}
                        >
                          <span
                            className={`absolute top-1 left-1 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                              selectedUser.isActive ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>
                      <p className="text-slate-500 dark:text-slate-400">
                        Création :{' '}
                        {new Date(selectedUser.createdAt).toLocaleString('fr-CA', {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}
